// path: src/pages/IPA/Negociacoes/pages/DetalhesDaNegociacao/components/AdicionarVerbaEmMassaModal/AdicionarVerbaEmMassaModal.tsx

import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { Notification, RadioGroup } from 'rsuite';
import Modal from 'rsuite/lib/Modal';
import { ButtonPrimary } from '../../../../../../../components';
import { Radio } from '../../../../../../../components/Radio';
import { notifyItemSaveFailure } from '../../../../hooks/notifyItemSaveFailure';
import {
  ESTADO_INICIAL_DA_MASSA,
  TEXTO_DA_MASSA,
  digitarOValorDaMassa,
  erroDoCampoDaMassa,
  escolhaDaMassa,
  escolherOModoDaMassa,
  houveRecusaNaMassa,
  identidadeDoItemDaMassa,
  linhaDaRecusaDaMassa,
  linhaDosAplicadosDaMassa,
  podeAplicarAMassa,
  registrarOResultadoDaMassa,
  subtituloDaMassa,
  temCampoDeValor,
} from '../../../NegociacaoFornecedor/verbaEmMassa';
import type { EscolhaDaMassa, EstadoDaMassa, ItemRecusadoNaMassa, ModoDaMassa, ResultadoDaMassa } from '../../../NegociacaoFornecedor/verbaEmMassa';
import styles from './AdicionarVerbaEmMassaModal.module.scss';

const ID_DO_CAMPO = 'verba-em-massa-valor';
const ID_DO_ERRO = 'verba-em-massa-erro';

const SEM_VALOR = '--';

const moeda = (valor: number | null | undefined): string => (valor == null ? SEM_VALOR : `R$ ${valor.toFixed(2).replace('.', ',')}`);

/**
 * Os dois números que não fecharam, do jeito que a recusa precisa ser lida: a verba já convertida ao
 * custo líquido contra o novo custo líquido daquele item. Sem os dois lado a lado, a frase da recusa
 * afirma uma comparação que o comprador não tem como conferir.
 */
const comparacaoDaRecusa = (item: ItemRecusadoNaMassa): string =>
  `verba no líquido ${moeda(item.sellinAllowance)} ≥ novo custo líquido ${moeda(item.newNetCost)}`;

export type AdicionarVerbaEmMassaModalProps = {
  show?: boolean;
  /** Quantos itens a seleção tem. É a contagem do subtítulo, e o modal não tem uma segunda conta dela. */
  quantidadeSelecionada: number;
  /**
   * Leva ao servidor o que o comprador decidiu aqui — o modo e, no modo de valor único, a verba por
   * unidade —, e devolve o resultado item a item.
   *
   * **O modal não monta corpo de requisição.** Dentro de uma negociação salva a seleção é uma
   * lista de identificadores de item; na tela de busca de fornecedor ela é o filtro mais o que
   * está marcado mais o "selecionar tudo". Quem sabe montá-la é a tela, que já a monta para as
   * outras ações da própria barra.
   */
  enviar: (escolha: EscolhaDaMassa) => Promise<ResultadoDaMassa>;
  onHide?: () => void;
  /** O que voltou, para quem atualiza a célula da linha na grade. */
  onAplicado?: (resultado: ResultadoDaMassa) => void;
  className?: string;
};

/**
 * O modal que aplica verba de sell-in aos itens marcados na grade.
 *
 * **As duas opções são um grupo de rádio controlado por valor, e não caixas de marcar**, que
 * permitiriam o estado em que as duas estão marcadas — e a marca de selecionado da caixa solta do
 * rsuite não chega ao DOM.
 *
 * A decisão de o que aplicar, o que é valor válido e o que vai no pedido mora no módulo de estado
 * ao lado do campo da verba na linha; este componente desenha, chama e encaminha.
 *
 * **Ele não recarrega a grade:** a resposta vem por item, e é ela que atualiza as células.
 */
export const AdicionarVerbaEmMassaModal = ({
  show,
  quantidadeSelecionada,
  enviar,
  onHide,
  onAplicado,
  className,
}: AdicionarVerbaEmMassaModalProps): JSX.Element => {
  const [estado, setEstado] = useState<EstadoDaMassa>(ESTADO_INICIAL_DA_MASSA);
  const [aplicando, setAplicando] = useState(false);

  // Fechar e reabrir volta ao início. Reabrir no modo e no valor da vez anterior ofereceria uma
  // escolha que o comprador fez para outra seleção.
  useEffect(() => {
    if (!show) setEstado(ESTADO_INICIAL_DA_MASSA);
  }, [show]);

  const erroDoCampo = erroDoCampoDaMassa(estado);
  const comCampo = temCampoDeValor(estado);
  const comRecusa = houveRecusaNaMassa(estado.resultado);
  const aplicados = estado.resultado?.applied ?? [];
  const recusados = estado.resultado?.rejected ?? [];

  const aplicar = async () => {
    // Seleção vazia não vira chamada, e a recusa mora aqui e não no manipulador do clique.
    if (quantidadeSelecionada === 0) return;

    const escolha = escolhaDaMassa(estado);

    if (!escolha) return;

    const modo: ModoDaMassa = escolha.mode;

    try {
      setAplicando(true);

      const resultado = await enviar(escolha);

      // As células das linhas aplicadas primeiro: é o que o comprador encontra atrás do modal.
      onAplicado?.(resultado);

      if (houveRecusaNaMassa(resultado)) {
        setEstado(anterior => registrarOResultadoDaMassa(anterior, resultado));
        return;
      }

      Notification.success({
        description: modo === 'IDEAL_PER_ITEM' ? TEXTO_DA_MASSA.SUCESSO_DA_IDEAL : TEXTO_DA_MASSA.SUCESSO_DO_MESMO_VALOR,
      });
      onHide?.();
    } catch (erro) {
      // Falha TOTAL é diferente da recusa parcial: nada foi gravado, o modal permanece aberto e a
      // seleção fica intacta.
      await notifyItemSaveFailure(erro);
    } finally {
      setAplicando(false);
    }
  };

  return (
    <Modal className={classNames('info-modal', styles['verba-em-massa-modal'], className)} show={show} onHide={onHide} backdrop="static">
      <Modal.Header>
        <div className={styles['cabecalho']}>
          <Modal.Title className={styles['titulo']}>{TEXTO_DA_MASSA.TITULO}</Modal.Title>
          <p className={styles['subtitulo']}>{subtituloDaMassa(quantidadeSelecionada)}</p>
        </div>
      </Modal.Header>

      <Modal.Body className={styles['corpo']}>
        {comRecusa ? (
          <section className={styles['resultado']}>
            {/*
              As duas contagens ficam JUNTAS e FORA do que rola: só uma delas deixaria o comprador sem
              saber o tamanho do que não pegou, e um resumo que rola para fora da tela é a mesma
              informação escondida.
            */}
            <p className={styles['resultado-aplicados']}>{`✔ ${linhaDosAplicadosDaMassa(aplicados.length)}`}</p>
            <p className={styles['resultado-recusados']}>{`⚠ ${linhaDaRecusaDaMassa(recusados.length)}`}</p>

            <ul className={styles['resultado-lista']} data-testid="verba-em-massa-recusados">
              {/* A recusa vinda da visão rede não tem identificador de linha: quem a nomeia é o código
                  do produto, pela mesma regra que casa o item aplicado com a célula. */}
              {recusados.map(item => (
                <li
                  key={identidadeDoItemDaMassa(item) ?? item.description ?? SEM_VALOR}
                  className={styles['resultado-item']}
                  data-testid={`verba-em-massa-recusado-${identidadeDoItemDaMassa(item) ?? SEM_VALOR}`}
                >
                  <span className={styles['resultado-item-produto']}>
                    <span className={styles['resultado-item-codigo']}>{item.productId ?? SEM_VALOR}</span>
                    <span className={styles['resultado-item-descricao']}>{item.description ?? SEM_VALOR}</span>
                  </span>
                  <span className={styles['resultado-item-numeros']}>{comparacaoDaRecusa(item)}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : (
          <>
            <RadioGroup
              name="verba-em-massa-modo"
              value={estado.modo}
              onChange={(valor: ModoDaMassa) => setEstado(anterior => escolherOModoDaMassa(anterior, valor))}
              className={styles['opcoes']}
            >
              <Radio value="IDEAL_PER_ITEM" isSelected={estado.modo === 'IDEAL_PER_ITEM'} className={styles['opcao']}>
                <span className={styles['opcao-titulo']}>{TEXTO_DA_MASSA.OPCAO_IDEAL}</span>
                <span className={styles['opcao-descricao']}>{TEXTO_DA_MASSA.DESCRICAO_IDEAL}</span>
              </Radio>

              <Radio value="SAME_VALUE_FOR_ALL" isSelected={estado.modo === 'SAME_VALUE_FOR_ALL'} className={styles['opcao']}>
                <span className={styles['opcao-titulo']}>{TEXTO_DA_MASSA.OPCAO_MESMO_VALOR}</span>
                <span className={styles['opcao-descricao']}>{TEXTO_DA_MASSA.DESCRICAO_MESMO_VALOR}</span>
              </Radio>
            </RadioGroup>

            {comCampo ? (
              <div className={styles['sub-opcao']}>
                <label className={styles['campo-rotulo']} htmlFor={ID_DO_CAMPO}>
                  {TEXTO_DA_MASSA.ROTULO_DO_CAMPO}
                </label>

                <div className={classNames(styles['campo'], erroDoCampo ? styles['campo-com-erro'] : '')}>
                  <span className={styles['campo-prefixo']}>{TEXTO_DA_MASSA.PREFIXO_DO_CAMPO}</span>
                  {/*
                    O exemplo vive no `placeholder`, e nunca no valor: valor pre-digitado seria um
                    acordo que o comprador nao escolheu e que o aplicar levaria ao servidor.
                  */}
                  <input
                    id={ID_DO_CAMPO}
                    className={styles['campo-entrada']}
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder={TEXTO_DA_MASSA.EXEMPLO_DO_CAMPO}
                    value={estado.textoDoValor ?? ''}
                    aria-invalid={Boolean(erroDoCampo)}
                    aria-describedby={erroDoCampo ? ID_DO_ERRO : undefined}
                    onChange={event => setEstado(anterior => digitarOValorDaMassa(anterior, event.target.value))}
                  />
                </div>

                {erroDoCampo ? (
                  <span className={styles['campo-erro']} id={ID_DO_ERRO} role="alert">
                    {erroDoCampo}
                  </span>
                ) : null}

                <p className={styles['aviso-informativo']}>{TEXTO_DA_MASSA.AVISO_DA_CONVERSAO}</p>
              </div>
            ) : (
              <p className={styles['aviso-atencao']}>{TEXTO_DA_MASSA.AVISO_DA_IDEAL}</p>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <ButtonPrimary skin="blue" theme="ghost" type="button" disabled={aplicando} onClick={onHide}>
          {TEXTO_DA_MASSA.FECHAR}
        </ButtonPrimary>
        {/*
          Com o resultado na tela o aplicar sai de cena: a aplicacao ja aconteceu sobre esta selecao, e
          um segundo clique sobre a mesma lista repetiria a gravacao dos que deram certo.
        */}
        {comRecusa ? null : (
          <ButtonPrimary
            skin="blue"
            theme="filled"
            type="button"
            isLoading={aplicando}
            disabled={!podeAplicarAMassa(estado)}
            onClick={() => {
              void aplicar();
            }}
          >
            {TEXTO_DA_MASSA.APLICAR}
          </ButtonPrimary>
        )}
      </Modal.Footer>
    </Modal>
  );
};
